




































































































































































































































































import axios from 'axios';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';

import {
    parse as parsedatefns,
    format,
    addDays,
    setHours,
    setMinutes,
    setSeconds,
    isAfter,
    differenceInHours,
    differenceInMinutes,
    addMinutes,
    subMinutes,
    startOfWeek,
    startOfDay,
    subDays,
    isSameDay,
} from 'date-fns';

import { clone, get } from 'lodash';
import CoDateRangePicker from '@/components/Atoms/co-date-range-picker/CoDateRangePicker.vue';
import { settlePromises } from '@/utils/promise';
import CoHeadline from '../../Atoms/co-headline/CoHeadline.vue';
import EventBus from '../../../eventBus';
import CoBookingCheckoutForUserV2 from '../co-booking-checkout-for-user-v2/CoBookingCheckoutForUserV2.vue';
import { BookingGranularityType, CheckOutDayType } from '../co-booking-resource/models.ts';
import CoButton from '../../Atoms/co-button/CoButton.vue';
import CoSkeleton from '../../Atoms/co-skeleton/CoSkeleton.vue';
import CoButtonGroup from '../../Molecules/co-button-group/CoButtonGroup.vue';

interface Slot {
    start: Date;
    end: Date;
    resource: any;
    free: boolean;
}

export default {
    name: 'CoBookingCalendarEnduser',
    components: {
        FullCalendar,
        CoBookingCheckoutForUserV2,
        CoHeadline,
        CoButton,
        CoSkeleton,
        CoButtonGroup,
        CoDateRangePicker,
    },
    props: {
        // resources to be displayed in the calendar
        resources: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            // fullcalendar options
            calendarOptions: {
                firstDay: 1,
                selectable: true,
                editable: false,
                businessHours: {
                    daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // Monday - Friday
                    startTime: '00:00', // a start time (10am in this example)
                    endTime: '24:00',
                },
                height: '72vh',
                nowIndicator: true,
                expandRows: true,
                longPressDelay: 250,
                plugins: [
                    dayGridPlugin,
                    timeGridPlugin,
                    listPlugin,
                    interactionPlugin, // needed for dateClick
                    dayGridPlugin,
                ],
                navLinks: false,
                headerToolbar: null,
                initialView: 'dayGridMonth',
                eventOverlap: false,
                slotLabelFormat: { hour12: false, hour: '2-digit', minute: '2-digit' },
                dayHeaderFormat: {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'numeric',
                },
                views: {
                    dayGridMonth: {
                        type: 'dayGrid',
                        dayHeaderContent: (args) =>
                            this.$t('datetime', {
                                date: args.date,
                                customFormat: 'E',
                            }),
                        showNonCurrentDates: true,
                        fixedWeekCount: true,
                    },
                    timeGridOneDay: {
                        type: 'timeGrid',
                        duration: { days: 1 },
                        dayHeaderContent: (args) =>
                            this.$t('datetime', {
                                date: args.date,
                                customFormat: 'E P',
                            }),
                    },
                    timeGridThreeDays: {
                        type: 'timeGrid',
                        duration: { days: 3 },
                        dayHeaderContent: (args) =>
                            this.$t('datetime', {
                                date: args.date,
                                customFormat: 'E P',
                            }),
                    },
                    timeGridWeek: {
                        type: 'timeGrid',
                        duration: { days: 7 },
                        dayHeaderContent: (args) =>
                            this.$t('datetime', {
                                date: args.date,
                                customFormat: 'E P',
                            }),
                        firstDay: 1,
                    },
                },
                selectMirror: true,
                dayMaxEvents: true,
                weekends: true,

                eventColor: '#5100FF',

                select: this.selectHandler,
                dateClick: this.dateClickHandler,
                selectAllow: this.selectAllow,
                // eventClick: this.handleEventClick,
                selectOverlap: this.selectOverlapCheck,
                datesSet: this.handleDatesSet,
                dayCellClassNames: this.getDayCellClassNames,

                slotDuration: '00:30:00',
                snapDuration: '00:30:00',

                allDaySlot: false,
                // set the scroll position time of the calendar to 90 minutes ago
                scrollTime: format(subMinutes(new Date(), 90), 'HH:mm:ss'),
            },
            selectedDate: new Date(),
            bookingCandidate: {
                start: null,
                end: null,
                resource: null,
                startTimeSlot: null as Slot | null,
                endTimeSlot: null as Slot | null,
                display: false,
            },

            freeBusySlots: [] as Slot[],
            windowWidth: 0,
            mobileView: false,
            slotsCancelRequests: [], // axios cancel tokens
            loadingFreeBusySlots: false,
            freeBusyRequestCycle: 0,

            calendarView: '',
            currentViewTitleKey: 0,
        };
    },
    mounted() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        window.addEventListener('resize', () => {
            that.windowWidth = window.innerWidth;
        });
        that.windowSizeHander();
        this.syncCalendarGranularityOptions();
        // set selectedDate to today
        this.selectedDate = new Date();
    },
    watch: {
        resources: {
            handler() {
                this.syncCalendarGranularityOptions();
                this.applyPreferredCalendarView();
                this.getFreeBusySlotsForResources();
                // this.calendarOptions.slotDuration = this.interval();
            },
            deep: true,
        },
    },

    computed: {
        primaryResource() {
            return this.resources && this.resources.length > 0 ? this.resources[0] : null;
        },
        isDayGranularity(): boolean {
            return this.primaryResource?.BookingGranularity === BookingGranularityType.BookingGranularityDay;
        },
        allowStartedTimeSlotBooking(): boolean {
            return !!this.primaryResource?.AllowStartedTimeSlotBooking;
        },
        dayBookingConfiguration() {
            return this.primaryResource?.DayBooking || null;
        },
        dayBookingCheckInMinutes(): number {
            const hour = this.dayBookingConfiguration?.CheckInTime?.Hour || 0;
            const minute = this.dayBookingConfiguration?.CheckInTime?.Minute || 0;
            return hour * 60 + minute;
        },
        dayBookingCheckOutMinutes(): number {
            const hour = this.dayBookingConfiguration?.CheckOutTime?.Hour || 0;
            const minute = this.dayBookingConfiguration?.CheckOutTime?.Minute || 0;
            return hour * 60 + minute;
        },
        isSplitDayBooking(): boolean {
            return (
                this.isDayGranularity &&
                this.dayBookingConfiguration?.CheckOutDay === CheckOutDayType.CheckOutDayFollowingDay &&
                this.dayBookingCheckOutMinutes <= this.dayBookingCheckInMinutes
            );
        },
    },

    methods: {
        // returns the title of the current view in forma Oct 31 – Nov 6, 2022
        getCurrentViewTitle() {
            // get calendar instance
            const { calendar } = this.$refs;
            if (!calendar) {
                return '';
            }
            const calendarApi = calendar.getApi();
            if (!calendarApi) {
                return '';
            }
            const { view, currentData } = calendarApi;
            // get start and end date of current view
            const start = view.activeStart;
            const end = view.activeEnd;

            if (start.getMonth() === end.getMonth()) {
                return this.$t('datetime', { date: start, customFormat: 'MMMM yyyy' });
            }

            // if its monthly view return only the month which is the current month
            if (view.type === 'dayGridMonth') {
                return this.$t('datetime', { date: currentData.viewTitle, customFormat: 'MMMM yyyy' });
            }

            return `${this.$t('datetime', { date: start, customFormat: 'MMM' })} - ${this.$t('datetime', {
                date: end,
                customFormat: 'MMM yyyy',
            })}`;
        },

        dayHeaderContent(args) {
            return this.$t('datetime', { date: args.date, customFormat: 'EEEEEE p asd' });
        },
        back() {
            this.$emit('back');
        },
        getSelectionIntervalInMinutes() {
            const resource = this.primaryResource;
            if (!resource || this.isDayGranularity) {
                return 30;
            }

            if (resource.BookingGranularity === BookingGranularityType.BookingGranularity15Minutes) {
                return 15;
            }

            if (resource.BookingGranularity === BookingGranularityType.BookingGranularityHour) {
                return 60;
            }

            if (resource.BookingGranularity === BookingGranularityType.BookingGranularity30Minutes) {
                return 30;
            }

            // Keep the visible calendar grid tied to booking granularity, not duration constraints.
            return 30;
        },
        interval() {
            const intervalInMinutes = this.getSelectionIntervalInMinutes();
            const intervalHours = Math.floor(intervalInMinutes / 60);
            const intervalMinutes = intervalInMinutes % 60;

            let result = ':00';
            if (intervalMinutes < 10) {
                result = `:0${intervalMinutes}${result}`;
            } else {
                result = `:${intervalMinutes}${result}`;
            }

            if (intervalHours < 10) {
                result = `0${intervalHours}${result}`;
            } else {
                result = `${intervalHours}${result}`;
            }

            return result;
        },
        syncCalendarGranularityOptions() {
            // Keep the visible time grid unchanged while letting drag selection snap to the resource interval.
            this.calendarOptions.snapDuration = this.interval();
            this.calendarOptions.slotDuration = this.interval();

            const { calendar } = this.$refs;
            if (!calendar) {
                return;
            }

            const calendarApi = calendar.getApi();
            calendarApi.setOption('snapDuration', this.calendarOptions.snapDuration);
            calendarApi.setOption('slotDuration', this.calendarOptions.slotDuration);
        },
        selectAllow(selectInfo) {
            const normalizedSelection = this.normalizeSelectionRange(selectInfo);
            if (!normalizedSelection) {
                return false;
            }

            if (!this.isDayGranularity) {
                const selectionDuration = differenceInMinutes(normalizedSelection.end, normalizedSelection.start);
                if (selectionDuration % this.getSelectionIntervalInMinutes() !== 0) {
                    return false;
                }
            }

            const now = new Date();
            if (
                isAfter(now, normalizedSelection.start) &&
                !this.isStartedBookingSelectionAllowed(normalizedSelection.start, normalizedSelection.end)
            ) {
                EventBus.$emit('ERROR', {
                    Message: this.$t('messages.booking.errorPastTime'),
                });
                return false;
            }
            if (this.isDayGranularity) {
                return this.isRangeCoveredByFreeSlots(normalizedSelection.start, normalizedSelection.end);
            }
            return true;
        },

        bookingCreated() {
            this.bookingCandidate.display = false;
            this.getFreeBusySlotsForResources();
        },
        dateClickHandler(clickInfo) {
            if (!this.isDayGranularity && clickInfo.allDay) {
                return;
            }

            const selectionStart = new Date(clickInfo.date);
            // Day-based resources should treat a month-cell click as selecting one booking day.
            const selectionEnd = this.isDayGranularity
                ? addDays(selectionStart, 1)
                : addMinutes(selectionStart, this.getSelectionIntervalInMinutes());
            const selectionInfo = {
                start: selectionStart,
                end: selectionEnd,
            };

            // A single click should preselect one booking interval while drag-selection continues to use FullCalendar.
            if (!this.selectAllow(selectionInfo)) {
                return;
            }

            this.selectHandler(selectionInfo);
        },

        selectHandler(event) {
            let { start, end } = event;
            const normalizedSelection = this.normalizeSelectionRange(event);
            if (!normalizedSelection) {
                return;
            }

            start = normalizedSelection.start;
            end = normalizedSelection.end;

            // find time slot in freeBusySlots array that matches the selected time slot
            const startTimeSlot = this.freeBusySlots.find(
                (slot) => slot.start.getTime() <= start.getTime() && slot.end.getTime() >= start.getTime() && slot.free
            );

            const endTimeSlot = this.freeBusySlots.find(
                (slot) => slot.start.getTime() <= end.getTime() && slot.end.getTime() >= end.getTime() && slot.free
            );

            const resource = this.resources[0];
            if (resource.BookWholeSlot) {
                // if resource.BookWholeSlot is true, then the whole time slot must be booked
                const selectedSlot = this.freeBusySlots.find(
                    (slot) =>
                        slot.start.getTime() <= start.getTime() && slot.end.getTime() >= end.getTime() && slot.free
                );

                if (!selectedSlot) return;
                start = selectedSlot.start;
                end = selectedSlot.end;
            }

            if (resource.MinBookingDuration) {
                const minBookingDuration = resource.MinBookingDuration;

                const duration = differenceInMinutes(end, start);
                // add minimum booking duration to start time
                const minBookingEnd = addMinutes(start, minBookingDuration);

                if (duration < minBookingDuration) {
                    end = minBookingEnd;
                    //     EventBus.$emit('ERROR', {
                    //         Message: `This resource requires a minimum booking duration of ${minBookingDuration} minutes`,
                    //     });
                    //     return;
                }
            }

            if (startTimeSlot && endTimeSlot) {
                this.bookingCandidate = {
                    start,
                    end,
                    resource,
                    startTimeSlot,
                    endTimeSlot,
                    display: true,
                };
            } else {
                EventBus.$emit('ERROR', {
                    Message: 'You cannot book outside of the available time slots',
                });
            }
        },
        normalizeSelectionRange(selectionInfo) {
            if (!this.isDayGranularity) {
                return {
                    start: selectionInfo.start,
                    end: selectionInfo.end,
                };
            }

            if (!this.primaryResource) {
                return null;
            }

            const start = this.applyDayBookingTime(selectionInfo.start, 'checkin');
            let endDate = selectionInfo.end;
            if (this.dayBookingConfiguration?.CheckOutDay === CheckOutDayType.CheckOutDaySameDay) {
                // Same-day resources treat the selected calendar day as the booked day itself.
                endDate = subDays(endDate, 1);
            }

            const end = this.applyDayBookingTime(endDate, 'checkout');
            if (!isAfter(end, start)) {
                return null;
            }

            return {
                start,
                end,
            };
        },
        buildDayBookingRangeFromStartDate(startDate: Date) {
            const normalizedStartDate = startOfDay(startDate);
            const endDate =
                this.dayBookingConfiguration?.CheckOutDay === CheckOutDayType.CheckOutDayFollowingDay
                    ? addDays(normalizedStartDate, 1)
                    : normalizedStartDate;

            return {
                start: this.applyDayBookingTime(normalizedStartDate, 'checkin'),
                end: this.applyDayBookingTime(endDate, 'checkout'),
            };
        },
        applyDayBookingTime(date: Date, mode: 'checkin' | 'checkout') {
            const baseDate = new Date(date);
            const hours =
                mode === 'checkin'
                    ? this.dayBookingConfiguration?.CheckInTime?.Hour || 0
                    : this.dayBookingConfiguration?.CheckOutTime?.Hour || 0;
            const minutes =
                mode === 'checkin'
                    ? this.dayBookingConfiguration?.CheckInTime?.Minute || 0
                    : this.dayBookingConfiguration?.CheckOutTime?.Minute || 0;

            let normalizedDate = setHours(baseDate, hours);
            normalizedDate = setMinutes(normalizedDate, minutes);
            normalizedDate = setSeconds(normalizedDate, 0);
            return normalizedDate;
        },
        isStartedBookingSelectionAllowed(start: Date, end: Date) {
            if (!this.allowStartedTimeSlotBooking) {
                return false;
            }

            const now = new Date();
            // Only keep the currently running interval/day selectable; fully past selections stay unavailable.
            return start.getTime() <= now.getTime() && end.getTime() > now.getTime();
        },
        getCurrentStartedBookingBoundary(now = new Date()): Date | null {
            if (!this.allowStartedTimeSlotBooking || !this.primaryResource) {
                return null;
            }

            if (!this.isDayGranularity) {
                const intervalInMinutes = this.getSelectionIntervalInMinutes();
                const minutesSinceStartOfDay = differenceInMinutes(now, startOfDay(now));
                return addMinutes(
                    startOfDay(now),
                    Math.floor(minutesSinceStartOfDay / intervalInMinutes) * intervalInMinutes
                );
            }

            const candidateStartDates = [startOfDay(now)];
            if (this.dayBookingConfiguration?.CheckOutDay === CheckOutDayType.CheckOutDayFollowingDay) {
                candidateStartDates.push(subDays(startOfDay(now), 1));
            }

            const activeRange = candidateStartDates
                .map((candidateStartDate) => this.buildDayBookingRangeFromStartDate(candidateStartDate))
                .find((range) => range.start.getTime() <= now.getTime() && range.end.getTime() > now.getTime());

            return activeRange?.start || null;
        },
        getRelevantFreeSlots(start: Date, end: Date) {
            return this.freeBusySlots
                .filter(
                    (slot) =>
                        slot.free &&
                        slot.resource?.Id === this.primaryResource?.Id &&
                        slot.end.getTime() > start.getTime() &&
                        slot.start.getTime() < end.getTime()
                )
                .sort((left, right) => left.start.getTime() - right.start.getTime());
        },
        isRangeCoveredByFreeSlots(start: Date, end: Date) {
            const relevantSlots = this.getRelevantFreeSlots(start, end);
            if (!relevantSlots.length) {
                return false;
            }

            let coveredUntil = start;
            for (let i = 0; i < relevantSlots.length; i += 1) {
                const slot = relevantSlots[i];
                // Day bookings may span several daily free-slot responses, so gaps mean the range crosses another booking.
                if (slot.start.getTime() > coveredUntil.getTime()) {
                    return false;
                }

                if (slot.end.getTime() > coveredUntil.getTime()) {
                    coveredUntil = slot.end;
                }

                if (coveredUntil.getTime() >= end.getTime()) {
                    return true;
                }
            }

            return false;
        },

        windowSizeHander() {
            this.windowWidth = window.innerWidth;

            // set the calendart container height
            const availableScreenHeight = window.innerHeight;
            const paddingFromBottom = 25;
            const calendarHeight =
                availableScreenHeight - paddingFromBottom - this.$refs.calendarcontainer.getBoundingClientRect().top;
            this.$refs.calendarcontainer.style.height = `${calendarHeight}px`;
            // set the calendar to fill the containers height
            this.calendarOptions.height = '100%';

            if (this.isDayGranularity) {
                this.calendarView = 'dayGridMonth';
                const { calendar } = this.$refs;
                if (calendar) {
                    calendar.getApi().changeView('dayGridMonth');
                }
                this.mobileView = this.windowWidth < 992;
                return;
            }

            if (this.windowWidth < 992) {
                // set calendar view to day view
                // this.calendarOptions.initialView = 'timeGridThreeDays';
                this.calendarView = 'timeGridThreeDays';

                const { calendar } = this.$refs;
                if (calendar) {
                    calendar.getApi().changeView('timeGridThreeDays');
                    this.calendarView = 'timeGridThreeDays';
                }
                this.mobileView = true;
            } else {
                // this.calendarOptions.initialView = 'timeGridWeek';
                this.calendarView = 'timeGridWeek';
                const { calendar } = this.$refs;
                if (calendar) {
                    calendar.getApi().changeView('timeGridWeek');
                    this.calendarView = 'timeGridWeek';
                }
                this.mobileView = false;
            }
        },
        applyPreferredCalendarView() {
            this.$nextTick(() => {
                this.windowSizeHander();
            });
        },

        selectResourceToBook(resource) {
            this.bookingCandidate.selectResourceToBook = false;
            this.bookingCandidate.resource = resource;
        },
        // calendar control
        onContext(ctx) {
            if (!ctx || !ctx.startDate) return;
            this.cancelAllOngoingRequests();
            this.selectedDate = ctx.startDate;
            const { calendar } = this.$refs;
            if (!calendar) return;
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.gotoDate(this.selectedDate);
            this.getFreeBusySlotsForResources();
        },
        setDateToday() {
            this.cancelAllOngoingRequests();
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.gotoDate(new Date());
        },
        next() {
            this.cancelAllOngoingRequests();
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.next();
        },
        prev() {
            this.cancelAllOngoingRequests();
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.prev();
        },
        changeView(view) {
            const nextView = this.isDayGranularity ? 'dayGridMonth' : view;
            this.calendarView = nextView;
            this.cancelAllOngoingRequests();
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.changeView(nextView);
        },

        cancelAllOngoingRequests() {
            // Cancel all existing requests and set their cancel functions to null
            for (let i = 0; i < this.slotsCancelRequests.length; i += 1) {
                if (this.slotsCancelRequests[i]) {
                    this.slotsCancelRequests[i]('Canceled due to new request');
                    this.$set(this.slotsCancelRequests, i, null);
                }
            }
        },

        /**
         * Called when the user selects a date range in the calendar view by clicking
         * previous/next buttons, today or a view change (day/week/3days)
         */
        handleDatesSet(dateInfo) {
            if (this.isDayGranularity) {
                this.currentViewTitleKey += 1;
                this.getFreeBusySlotsForResources();
                return;
            }

            if (dateInfo && dateInfo.view && dateInfo.view.type === 'timeGridWeek' && dateInfo.start) {
                const monday = startOfWeek(dateInfo.start, { weekStartsOn: 1 });
                if (dateInfo.start.getDay() !== 1) {
                    const { calendar } = this.$refs;
                    if (calendar) {
                        calendar.getApi().gotoDate(monday);
                    }
                }
            }
            this.currentViewTitleKey += 1;
            this.getFreeBusySlotsForResources();
        },

        getVisibleDateRange() {
            const { calendar } = this.$refs;
            if (!calendar) {
                // return default date range
                const today = new Date();
                const start = Math.floor(today.getTime() / 1000);
                const end = start + 3600 * 24 * 7;
                return { start, end };
            }

            const calendarApi = this.$refs.calendar.getApi();
            const { view } = calendarApi;

            // Use the visible grid range so month view also fetches leading/trailing non-current dates.
            const startUTC = view.activeStart;
            // Month view needs one extra day so the trailing edge can validate overnight/day-based availability.
            const endUTC = this.isMonthlyView() ? addDays(view.activeEnd, 1) : view.activeEnd;

            const start = Math.floor(startUTC.getTime() / 1000);
            const end = Math.floor(endUTC.getTime() / 1000);
            return { start, end };
        },
        isMonthlyView() {
            const { calendar } = this.$refs;
            if (!calendar) {
                return this.calendarView === 'dayGridMonth' || this.calendarOptions.initialView === 'dayGridMonth';
            }

            return calendar.getApi().view.type === 'dayGridMonth';
        },

        getFreeBusySlotsForResources() {
            const { start, end } = this.getVisibleDateRange();
            // get list dates in the visible range
            const dates = this.eachDayOfInterval(new Date(start * 1000), new Date(end * 1000));
            const requestCycle = this.freeBusyRequestCycle + 1;
            const stagedFreeBusySlots: Slot[] = [];
            const requests = [];

            this.freeBusyRequestCycle = requestCycle;
            this.loadingFreeBusySlots = this.resources.length > 0 && dates.length > 0;
            this.freeBusySlots = [];
            this.cleanCalendar();

            if (!this.loadingFreeBusySlots) {
                this.loadingFreeBusySlots = false;
                if (!this.isMonthlyView()) {
                    this.greyoutEventForPast(new Date(start * 1000), new Date(end * 1000));
                }
                return;
            }

            if (this.isMonthlyView()) {
                this.resources.forEach((resource, resourceIndex) => {
                    requests.push(
                        this.getMonthlySlots(resource, resourceIndex, dates, start, end, stagedFreeBusySlots)
                    );
                });
            } else {
                this.resources.forEach((resource, resourceIndex) => {
                    dates.forEach((date, dateIndex) => {
                        const requestIndex = resourceIndex * dates.length + dateIndex;
                        requests.push(this.getSlots(date, resource, requestIndex, stagedFreeBusySlots));
                    });
                });
            }

            // Render the visible range in one pass so split-day logic never evaluates partial data.
            settlePromises(requests).then(() => {
                if (requestCycle !== this.freeBusyRequestCycle) {
                    return;
                }

                this.renderFreeBusySlots(stagedFreeBusySlots, start, end);
                this.loadingFreeBusySlots = false;
            });
        },

        convertFreeSlotToBusySlots(slots: Slot[], date: Date, resource: any): Slot[] {
            const busySlots: Slot[] = [];
            let busyStart = null;
            let busyEnd = null;

            if (slots.length === 0) {
                // no free slot means the whole day is busy
                busyStart = setHours(date, 0);
                busyStart = setMinutes(busyStart, 0);
                busyStart = setSeconds(busyStart, 0);

                busyEnd = addDays(busyStart, 1);
                busyEnd = setHours(busyEnd, 0);
                busyEnd = setMinutes(busyEnd, 0);
                busyEnd = setSeconds(busyEnd, 0);

                const busySlot: Slot = {
                    start: busyStart,
                    end: busyEnd,
                    free: false,
                    resource,
                };
                busySlots.push(busySlot);

                return busySlots;
            }

            // if there is only one free slot and it is the whole day, then there is no busy slot
            if (slots.length === 1) {
                const { start } = slots[0];
                const { end } = slots[0];

                const diffHours = differenceInHours(end, start);
                if (diffHours === 24) {
                    // whole day free slot
                    return busySlots;
                }

                if (
                    start.getHours() === 0 &&
                    start.getMinutes() === 0 &&
                    (end.getHours() !== 0 || end.getMinutes() !== 0)
                ) {
                    // free slot from day start to some time
                    busyEnd = addDays(end, 1);
                    busyEnd = setHours(busyEnd, 0);
                    busyEnd = setMinutes(busyEnd, 0);
                    busyEnd = setSeconds(busyEnd, 0);
                    const busySlot: Slot = {
                        start: end,
                        end: busyEnd,
                        resource: slots[0].resource,
                        free: false,
                    };
                    busySlots.push(busySlot);
                }

                if (
                    (start.getHours() !== 0 || start.getMinutes() !== 0) &&
                    end.getHours() === 0 &&
                    end.getMinutes() === 0
                ) {
                    busyStart = start;
                    busyStart = setHours(busyStart, 0);
                    busyStart = setMinutes(busyStart, 0);
                    busyStart = setSeconds(busyStart, 0);
                    // free slot from some time to day end
                    const busySlot: Slot = {
                        start: busyStart,
                        end: start,
                        resource: slots[0].resource,
                        free: false,
                    };
                    busySlots.push(busySlot);
                }

                if (busySlots.length > 0) return busySlots;
            }
            slots.forEach((slot, index) => {
                // this adds's a busy slot before free current slot if busy slot is not null
                if (busyStart !== null) {
                    // if last slot from day start to slot start
                    busyEnd = slot.start;
                    const busySlot: Slot = {
                        start: busyStart,
                        end: busyEnd,
                        resource: slot.resource,
                        free: false,
                    };
                    busySlots.push(busySlot);
                    busyStart = slot.end;
                    busyEnd = null;
                }

                if (index === 0) {
                    // first slot from day start to slot start
                    busyStart = slot.start;
                    // day start for busyStart
                    busyStart = setHours(busyStart, 0);
                    busyStart = setMinutes(busyStart, 0);
                    busyStart = setSeconds(busyStart, 0);

                    busyEnd = slot.start;
                    const res = clone(slot.resource);
                    // res.Color = '#ddd';
                    if (busyStart.getTime() !== busyEnd.getTime()) {
                        const busySlot: Slot = {
                            start: busyStart,
                            end: busyEnd,
                            resource: res,
                            free: false,
                        };
                        busySlots.push(busySlot);
                    }

                    busyStart = slot.end;
                }

                if (index === slots.length - 1) {
                    // if last slot from slot end to day end means there is no any busy slot for this day
                    busyStart = slot.end;
                    // this checks if last free slot is till day end or not
                    // if not, then we need to add a busy slot from last free slot end to day end
                    if (busyStart.getHours() !== 0 || busyStart.getMinutes() !== 0) {
                        busyEnd = slot.end;
                        busyEnd = addDays(busyEnd, 1);

                        busyEnd = setHours(busyEnd, 0);
                        busyEnd = setMinutes(busyEnd, 0);
                        busyEnd = setSeconds(busyEnd, 0);
                        const res = clone(slot.resource);
                        const busySlot: Slot = {
                            start: busyStart,
                            end: busyEnd,
                            resource: res,
                            free: false,
                        };

                        if (busyStart.getTime() !== busyEnd.getTime()) {
                            busySlots.push(busySlot);
                        }
                        busyStart = null;
                        busyEnd = null;
                    }
                }

                if (busyStart === null) {
                    // if slot start is not first slot
                    busyStart = slot.end;
                }
            });

            return busySlots;
        },

        cleanCalendar() {
            this.cancelAllOngoingRequests();
            const { calendar } = this.$refs;
            if (!calendar) return;
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.removeAllEvents();
        },
        eachDayOfInterval(start, end) {
            const dates = [];
            let currentDate = startOfDay(start);
            const endDate = startOfDay(end);
            while (currentDate <= endDate) {
                dates.push(currentDate);
                currentDate = addDays(currentDate, 1);
            }
            return dates;
        },
        renderFreeBusySlots(slots: Slot[], rangeStart: number, rangeEnd: number) {
            const sortedSlots = [...slots].sort((a, b) => a.start.getTime() - b.start.getTime());
            this.freeBusySlots = sortedSlots;

            sortedSlots.forEach((slot) => {
                this.addBackgroundEvent(slot, sortedSlots);
                if (!this.isDayGranularity && slot.free && slot.resource.MinBookingDuration) {
                    const minBookingDuration = slot.resource.MinBookingDuration;
                    const duration = differenceInMinutes(slot.end, slot.start);
                    if (duration < minBookingDuration) {
                        this.greyoutEventForBlockedSlot(slot.start, slot.end);
                    }
                }
            });

            if (this.isMonthlyView() && this.isDayGranularity && this.isSplitDayBooking && this.primaryResource) {
                const visibleDates = this.eachDayOfInterval(
                    new Date(rangeStart * 1000),
                    subDays(new Date(rangeEnd * 1000), 1)
                );

                visibleDates.forEach((date) => {
                    const hasVisibleBusySlotForDay = sortedSlots.some(
                        (slot) =>
                            !slot.free &&
                            slot.resource?.Id === this.primaryResource.Id &&
                            isSameDay(slot.start, date) &&
                            !this.shouldHideSplitDayBusySlot(slot, sortedSlots)
                    );
                    if (hasVisibleBusySlotForDay || !this.isSplitDayStartBlockedByNextDay(date)) {
                        return;
                    }

                    // Blocked start days should look like normal booked days because users cannot start an overnight stay there.
                    this.addBackgroundEvent(
                        {
                            start: startOfDay(date),
                            end: addDays(startOfDay(date), 1),
                            resource: this.primaryResource,
                            free: false,
                        },
                        sortedSlots
                    );
                });
            }

            // Month cells grey out past dates via day-cell classes so the date number stays readable above fills.
            if (this.isMonthlyView()) {
                return;
            }

            this.greyoutEventForPast(new Date(rangeStart * 1000), new Date(rangeEnd * 1000));
        },
        parseApiFreeSlots(entries = [], resource): Slot[] {
            const freeSlots: Slot[] = [];

            entries.forEach((entry) => {
                const startstr = (entry.StartTime || entry.startTime || '').replace('UTC', '');
                const endstr = (entry.EndTime || entry.endTime || '').replace('UTC', '');
                if (!startstr || !endstr) {
                    return;
                }
                const startdate = parsedatefns(startstr, 'yyyy-MM-dd HH:mm:ss XX', new Date());
                const enddate = parsedatefns(endstr, 'yyyy-MM-dd HH:mm:ss XX', new Date());
                const freeBusySlot: Slot = {
                    start: startdate,
                    end: enddate,
                    free: true,
                    resource,
                };

                freeSlots.push(freeBusySlot);
            });

            return freeSlots;
        },
        getSlots(date, resource, index, stagedFreeBusySlots: Slot[]) {
            // Create a new CancelToken
            const cancelToken = axios.CancelToken;
            const source = cancelToken.source();

            // Store the cancel function for this ID
            this.$set(this.slotsCancelRequests, index, source.cancel);

            const dateStr = format(date, 'yyyy-MM-dd');

            return axios({
                method: 'GET',
                url: `/booking/resources/free-slots/${resource.Id}/${dateStr}`,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
                cancelToken: source.token,
            })
                .then((response) => {
                    const slots = get(response, 'data.FreeSlots', get(response, 'data.freeSlots', []));
                    const freeSlots = this.parseApiFreeSlots(slots, resource);

                    const busySlots = this.convertFreeSlotToBusySlots(freeSlots, date, resource);
                    stagedFreeBusySlots.push(...busySlots, ...freeSlots);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        // console.log(`Request #${index} canceled:`, error.message);
                    } else {
                        // console.log(error);
                    }
                })
                .finally(() => {
                    // Set the cancel function to null for the completed request
                    this.$set(this.slotsCancelRequests, index, null);
                });
        },
        getMonthlySlots(resource, index, dates: Date[], start: number, end: number, stagedFreeBusySlots: Slot[]) {
            const cancelToken = axios.CancelToken;
            const source = cancelToken.source();

            this.$set(this.slotsCancelRequests, index, source.cancel);

            return axios({
                method: 'POST',
                url: '/booking/resources/availability',
                withCredentials: true,
                data: {
                    resourceid: resource.Id,
                    resourceID: resource.Id,
                    spaceid: this.$store.state.space.ID,
                    spaceID: this.$store.state.space.ID,
                    start,
                    end,
                },
                headers: {
                    'Content-Type': 'application/json',
                },
                cancelToken: source.token,
            })
                .then((response) => {
                    const freeSlotsByDay = get(response, 'data.FreeSlots', get(response, 'data.freeSlots', {}));

                    // The monthly endpoint returns free slots grouped by date, so we rebuild each day
                    // before reusing the existing busy-slot conversion and rendering logic.
                    dates.forEach((date) => {
                        const dateKey = format(date, 'yyyy-MM-dd');
                        const slots = get(
                            freeSlotsByDay,
                            `${dateKey}.FreeSlots`,
                            get(freeSlotsByDay, `${dateKey}.freeSlots`, [])
                        );
                        const freeSlots = this.parseApiFreeSlots(slots, resource);
                        const busySlots = this.convertFreeSlotToBusySlots(freeSlots, date, resource);
                        stagedFreeBusySlots.push(...busySlots, ...freeSlots);
                    });
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        // console.log(`Request #${index} canceled:`, error.message);
                    } else {
                        // console.log(error);
                    }
                })
                .finally(() => {
                    this.$set(this.slotsCancelRequests, index, null);
                });
        },
        selectOverlapCheck() {
            return this.resources.length > 1;
        },

        handleEventClick(clickInfo) {
            if (clickInfo.event.source.id !== 'user') return;

            this.bookingComponent = clickInfo.event.extendedProps.originalObject;

            this.bookingComponentStartDate = clickInfo.event.start;
            this.bookingComponentEndDate = clickInfo.event.end;

            setTimeout(() => {
                this.$bvModal.show(`modal-booking-${clickInfo.event.id}`);
            }, 100);
        },

        greyoutEventForPast(start, end: Date) {
            const now = new Date();
            if (start > now) {
                // for future calendar nothing to grey out
                return;
            }

            const currentStartedBookingBoundary = this.getCurrentStartedBookingBoundary(now);
            let endOfPast = end;
            if (end > now) {
                endOfPast = now;
            }
            if (currentStartedBookingBoundary && endOfPast > currentStartedBookingBoundary) {
                // Leave the active started booking interval/day visually bookable when the resource allows it.
                endOfPast = currentStartedBookingBoundary;
            }
            if (endOfPast <= start) {
                return;
            }

            const { calendar } = this.$refs;
            if (!calendar) return;
            const calendarApi = this.$refs.calendar.getApi();
            // generate a random id
            const id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

            const event = {
                title: 'past',
                id,
                start,
                end: endOfPast,
                // display: slot.free ? 'inverse-background' : 'background',
                display: 'background',
                classNames: ['co-fc-busy-slot'],
                color: '#ddd',
            };

            // if daily granularity add another class for full day greyout
            if (this.isDayGranularity) {
                event.classNames.push('co-fc-day-busy-fill');
            }
            calendarApi.addEvent(event);
        },
        greyoutEventForBlockedSlot(start, end: Date) {
            const { calendar } = this.$refs;
            if (!calendar) return;
            const calendarApi = this.$refs.calendar.getApi();
            // generate a random id
            const id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

            const event = {
                title: '',
                id,
                start,
                end,
                display: 'background',
                color: '#ddd',
                classNames: ['co-fc-busy-slot'],
                extendedProps: {
                    isInactive: true,
                },
            };

            // if daily granularity add another class for full day greyout
            if (this.isDayGranularity) {
                event.classNames.push('co-fc-day-busy-fill');
            }

            calendarApi.addEvent(event);
        },

        addBackgroundEvent(slot: Slot, daySlots: Slot[] = []) {
            if (!slot) return;
            if (!slot.resource) return;
            if (slot.free) return;
            if (this.shouldHideCoveredDayBusySlot(slot)) return;
            if (this.shouldHideSplitDayBusySlot(slot, daySlots)) return;

            const { calendar } = this.$refs;
            if (!calendar) return;
            const calendarApi = this.$refs.calendar.getApi();
            // generate a random id
            const id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

            const isDayGranularityBusyEvent = this.isDayGranularity;
            const title = isDayGranularityBusyEvent ? '' : this.$t('labels.coBookingCalendar.busy');
            const event = {
                title,
                groupId: slot.resource.Id,
                id,
                start: isDayGranularityBusyEvent ? startOfDay(slot.start) : slot.start,
                end: isDayGranularityBusyEvent ? addDays(startOfDay(slot.start), 1) : slot.end,
                display: isDayGranularityBusyEvent ? 'background' : 'auto',
                allDay: isDayGranularityBusyEvent,
                color: !slot.free ? slot.resource.Color : '#f00',
                backgroundColor: !slot.free ? slot.resource.Color : '#f00',
                borderColor: !slot.free ? slot.resource.Color : '#f00',
                classNames: isDayGranularityBusyEvent
                    ? ['co-fc-busy-slot', 'co-fc-day-busy-fill']
                    : ['co-fc-busy-slot'],
            };
            calendarApi.addEvent(event);
        },
        getBusySlotsForResourceDay(slot: Slot, daySlots: Slot[] = []) {
            return daySlots.filter(
                (entry) => !entry.free && entry.resource?.Id === slot.resource?.Id && isSameDay(entry.start, slot.start)
            );
        },
        shouldHideCoveredDayBusySlot(slot: Slot) {
            if (!this.isDayGranularity) {
                return false;
            }

            const bookingStart = this.applyDayBookingTime(slot.start, 'checkin');
            const checkOutDate =
                this.dayBookingConfiguration?.CheckOutDay === CheckOutDayType.CheckOutDayFollowingDay
                    ? addDays(slot.start, 1)
                    : slot.start;
            const bookingEnd = this.applyDayBookingTime(checkOutDate, 'checkout');
            if (!isAfter(bookingEnd, bookingStart)) {
                return false;
            }

            // Day bookings only need the configured booking window, so ignore busy time around it.
            return this.isRangeCoveredByFreeSlots(bookingStart, bookingEnd);
        },
        // Split-day checkout slots occupy only the morning of the current day.
        isSplitDayCheckOutBusySlot(slot: Slot) {
            if (!this.isSplitDayBooking) {
                return false;
            }

            const slotEndMinutes = slot.end.getHours() * 60 + slot.end.getMinutes();
            return (
                isSameDay(slot.start, slot.end) &&
                slot.start.getHours() === 0 &&
                slot.start.getMinutes() === 0 &&
                slotEndMinutes <= this.dayBookingCheckOutMinutes
            );
        },
        // Split-day checkin slots occupy only the afternoon/evening before midnight.
        isSplitDayCheckInBusySlot(slot: Slot) {
            if (!this.isSplitDayBooking) {
                return false;
            }

            const slotStartMinutes = slot.start.getHours() * 60 + slot.start.getMinutes();
            return (
                !isSameDay(slot.start, slot.end) &&
                slot.end.getHours() === 0 &&
                slot.end.getMinutes() === 0 &&
                slotStartMinutes >= this.dayBookingCheckInMinutes
            );
        },
        isSplitDayPartialBusySlot(slot: Slot) {
            return this.isSplitDayCheckOutBusySlot(slot) || this.isSplitDayCheckInBusySlot(slot);
        },
        // A split-day start date is blocked when its overnight stay cannot reach the next day's checkout window.
        isSplitDayStartBlockedByNextDay(date: Date) {
            if (!this.isSplitDayBooking || !this.primaryResource) {
                return false;
            }

            const normalizedDate = startOfDay(date);
            const nextDay = addDays(normalizedDate, 1);
            const hasNextDayData = this.freeBusySlots.some(
                (slot) => slot.resource?.Id === this.primaryResource.Id && isSameDay(slot.start, nextDay)
            );
            if (!hasNextDayData) {
                return false;
            }

            const bookingStart = this.applyDayBookingTime(normalizedDate, 'checkin');
            const bookingEnd = this.applyDayBookingTime(nextDay, 'checkout');
            return !this.isRangeCoveredByFreeSlots(bookingStart, bookingEnd);
        },
        shouldHideSplitDayBusySlot(slot: Slot, daySlots: Slot[] = []) {
            if (
                !this.isDayGranularity ||
                this.dayBookingConfiguration?.CheckOutDay !== CheckOutDayType.CheckOutDayFollowingDay
            ) {
                return false;
            }

            const dayBusySlots = this.getBusySlotsForResourceDay(slot, daySlots);
            if (!dayBusySlots.length || !this.isSplitDayPartialBusySlot(slot)) {
                return false;
            }

            const hasSplitDayCheckIn = dayBusySlots.some((entry) => this.isSplitDayCheckInBusySlot(entry));
            const hasSplitDayCheckOut = dayBusySlots.some((entry) => this.isSplitDayCheckOutBusySlot(entry));

            // Checkout tails belong to the previous booking day, so they should not paint the current day as booked.
            return (
                hasSplitDayCheckOut &&
                !hasSplitDayCheckIn &&
                dayBusySlots.every((entry) => this.isSplitDayPartialBusySlot(entry))
            );
        },
        getDayCellClassNames(args) {
            const classNames = [];

            // Month view handles past dates with a cell overlay because background events can hide the day number.
            if (this.isMonthlyView() && args.date < startOfDay(new Date())) {
                classNames.push('co-fc-day-past');
            }

            if (!this.isDayGranularity || !this.isSplitDayBooking || !this.primaryResource) {
                return classNames;
            }

            const slotsForDay = this.freeBusySlots.filter(
                (slot) =>
                    slot.resource?.Id === this.primaryResource.Id && isSameDay(slot.start, args.date) && !slot.free
            );
            if (!slotsForDay.length) {
                return classNames;
            }

            const hasOtherBusySlots = slotsForDay.some((slot) => !this.isSplitDayPartialBusySlot(slot));

            if (hasOtherBusySlots) {
                return classNames;
            }

            return classNames;
        },
    },
};
